import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { useDispatch, useSelector } from "react-redux";
import { getAdminHistory } from "../store/history/history.action";
import useTranslation from "../hooks/useTranslation";
import { pickerLocale, pickerDate } from "../util/pickerLocale";

// Parsed as local midnight: `new Date("2026-10-01")` shifts a day back for
// viewers west of Greenwich.
const parseDay = (value) => {
  const [y, m, d] = String(value || "").split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const TotalIncome = () => {
  const { history } = useSelector((state) => state.history);
  const { t, languageCode } = useTranslation();
  // Latin digits in every language, so the ledger stays comparable across locales.
  const locale = `${languageCode || "en"}-u-nu-latn`;
  const coins = (n) => Number(n || 0).toLocaleString(locale);
  const money = (n) =>
    Number(n || 0).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const [startDate, setStartDate] = useState(dayjs().startOf("month").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("month").toDate());

  const agencyId = localStorage.getItem("agencyId");

  const dispatch = useDispatch();

  const startAllDate = "1970-01-01";
  const endAllDate = dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    const startDate = dayjs().startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs().endOf("month").format("YYYY-MM-DD");
    dispatch(getAdminHistory(agencyId, startDate, endDate));
  }, [agencyId]);

  const handleApply = (event, picker) => {
    const start = dayjs(picker.startDate).format("YYYY-MM-DD");
    const end = dayjs(picker.endDate).format("YYYY-MM-DD");

    setStartDate(start);
    setEndDate(end);

    dispatch(getAdminHistory(agencyId, start, end));
  };

  return (
    <>
      <div className="page-container">
        <div className="page-content">
          <div className="main-wrapper ps-0">
            <div className="main-section">
              <div
                className="row mb-2"
                style={{
                  zIndex: "9",
                  position: "fixed",
                  width: "100%",
                  top: "0",
                  background: "var(--mx-surface)",
                  paddingTop: "15px",
                  paddingBottom: "15px",
                  paddingInlineStart: "22px",
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                }}
              >
                <div className="col-4 d-flex align-items-center">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => window.history.back()}
                  >
                    <path
                      d="M1.18529 11.648L7.60196 18.0647C7.77484 18.2317 8.0064 18.3241 8.24674 18.322C8.48709 18.3199 8.717 18.2235 8.88696 18.0535C9.05692 17.8836 9.15332 17.6537 9.15541 17.4133C9.1575 17.173 9.0651 16.9414 8.89812 16.7685L4.04621 11.9166H20.1667C20.4098 11.9166 20.643 11.82 20.8149 11.6481C20.9868 11.4762 21.0834 11.2431 21.0834 11C21.0834 10.7568 20.9868 10.5237 20.8149 10.3518C20.643 10.1799 20.4098 10.0833 20.1667 10.0833H4.04621L8.89812 5.23137C8.98568 5.14681 9.05551 5.04566 9.10355 4.93382C9.15159 4.82198 9.17688 4.7017 9.17794 4.57999C9.179 4.45827 9.1558 4.33757 9.10971 4.22491C9.06362 4.11226 8.99555 4.00991 8.90949 3.92384C8.82342 3.83777 8.72107 3.7697 8.60842 3.72361C8.49576 3.67752 8.37506 3.65433 8.25334 3.65539C8.13163 3.65645 8.01134 3.68173 7.8995 3.72978C7.78767 3.77782 7.68652 3.84765 7.60196 3.9352L1.18529 10.3519C1.01344 10.5238 0.916904 10.7569 0.916904 11C0.916904 11.243 1.01344 11.4761 1.18529 11.648Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div className="col-4 text-center">
                  <p
                    className="mb-0 text-white text-center"
                    style={{ fontSize: "20px", fontWeight: 500 }}
                  >
                    {t("income_history", "Income")}
                  </p>
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-end py-3 px-2"
                style={{ marginTop: "75px" }}
              >
                <div className="date-picker">
                    <DateRangePicker
                      initialSettings={{
                        startDate: undefined,
                        endDate: undefined,
                        ranges: {
                          [t("all")]: [
                            new Date("1970-01-01"),
                            dayjs().toDate(),
                          ],
                          [t("today")]: [dayjs().toDate(), dayjs().toDate()],
                          [t("yesterday")]: [
                            dayjs().subtract(1, "days").toDate(),
                            dayjs().subtract(1, "days").toDate(),
                          ],

                          [t("last_7_days")]: [
                            dayjs().subtract(6, "days").toDate(),
                            dayjs().toDate(),
                          ],
                          [t("last_30_days")]: [
                            dayjs().subtract(29, "days").toDate(),
                            dayjs().toDate(),
                          ],
                          [t("this_month")]: [
                            dayjs().startOf("month").toDate(),
                            dayjs().endOf("month").toDate(),
                          ],
                          [t("last_month")]: [
                            dayjs()
                              .subtract(1, "month")
                              .startOf("month")
                              .toDate(),
                            dayjs()
                              .subtract(1, "month")
                              .endOf("month")
                              .toDate(),
                          ],
                        },
                        maxDate: new Date(),
                        singleDatePicker: false,
                        linkedCalendars: false,
                        locale: pickerLocale(languageCode, t),
                      }}
                      // onCallback={handleCallback}
                      onApply={handleApply}
                    >
                      <input
                        type="text"
                        readOnly
                        placeholder={t("select_date_range")}
                        // onClick={handleInputClick}
                        className={`daterange float-right  mr-4  text-center`}
                        value={
                          (startDate === startAllDate &&
                            endDate === endAllDate) ||
                          (startDate === "All" && endDate === "All")
                            ? t("select_date_range")
                            : `${pickerDate(startDate, languageCode)} - ${pickerDate(
                                endDate,
                                languageCode,
                              )}`
                        }
                        style={{
                          // width: "85%",
                          fontWeight: 500,
                          cursor: "pointer",
                          background: "var(--mx-border)",
                          color: "var(--mx-text-2)",
                          display: "flex",
                          width: "100%",
                          justifyContent: "end",
                          fontSize: "13px",
                          padding: "10px",
                          maxWidth: "226px",
                          borderRadius: "64px",
                          border: "1px solid transparent",
                          height: "48px !important",
                        }}
                      />
                    </DateRangePicker>
                  </div>
                </div>

              {history?.length > 0 ? (
                <div className="px-2">
                  {history.map((data) => {
                    const start = parseDay(data?.startDate);
                    const end = parseDay(data?.endDate);
                    const fmt = (d, opts) =>
                      d ? new Intl.DateTimeFormat(locale, opts).format(d) : "";
                    return (
                      <div
                        className="col-12 mb-3 main-settlement"
                        key={data?.startDate}
                      >
                        <div className="main-settlement-header">
                          <div className="settlement-header-top">
                            <p className="date-content mb-0">
                              {fmt(start, { month: "short", day: "numeric" })} –{" "}
                              {fmt(end, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </p>
                            {data?.statusOfTransaction === 1 && (
                              <p className="status-content mb-0">
                                {t("withdraw_status_pending", "Pending")}
                              </p>
                            )}
                            {data?.statusOfTransaction === 2 && (
                              <p className="status-content-paid mb-0">
                                {t("withdraw_status_paid", "Paid")}
                              </p>
                            )}
                          </div>
                          <p className="dollar-content mb-0">
                            {coins(data?.finalAmountTotal)} (
                            {money(data?.dollar)} $)
                          </p>
                        </div>
                        <div className="main-settlement-body p-2">
                          <div className="row">
                            <div className="col-6">
                              <p className="text-main-12">{t("host_coins")}</p>
                              <p className="text-Light">{coins(data?.coinEarned)}</p>
                            </div>
                            <div
                              className="col-6"
                              style={{ borderInlineStart: "1px solid var(--mx-border)" }}
                            >
                              <p className="text-main-12">
                                {t("agency_commission")} (
                                {data?.agencyCommissionPercentage}%)
                              </p>
                              <p className="text-Light">
                                {coins(data?.commissionCoinEarned)}
                              </p>
                            </div>
                          </div>
                          <p
                            className="dollar-content mb-0 pb-2"
                            style={{ borderBottom: "1px solid var(--mx-border)" }}
                          >
                            {t(
                              "total_coins_calculation",
                              "Commission {{coinEarned}} + bonuses {{bonus}} = total {{total}}",
                              {
                                coinEarned: coins(data?.commissionCoinEarned),
                                bonus: coins(data?.bonusOrPenltyAmount),
                                total: coins(data?.finalAmountTotal),
                              },
                            )}
                          </p>
                        </div>
                        {data?.note && (
                          <p className="settlement-note mb-0 ms-3 me-3 mb-2">
                            {t("note")}: {data.note}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state px-3">
                  <p className="empty-state-title mb-1">{t("no_data_found")}</p>
                  <p className="empty-state-hint mb-0">
                    {t(
                      "income_empty_hint",
                      "A row appears for every month your hosts earn coins.",
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TotalIncome;
